import { StoryblokRichTextNode } from "@storyblok/richtext";
import type {
  RichTextImageBlock,
  RichTextImageAlignment,
} from "../types/RichTextImageBlock";
import { escapeHtml, sanitizeUrl } from "./sanitize";

// Extended version of a Storyblok rich text node that may contain blok data.
type StoryblokComponentNode = StoryblokRichTextNode<string> & {
  attrs?: {
    body?: unknown;
  };
};

type StoryblokComponentRenderer = (blok: Record<string, unknown>) => string;

// We rely on the backend type but keep a relaxed record to avoid runtime errors.
type GuardedRichTextImageBlock = RichTextImageBlock & Record<string, unknown>;

const allowedUnits = new Set(["px", "rem", "em", "vw", "vh", "%", "ch"]);

const toStringOrEmpty = (value: unknown): string => {
  return typeof value === "string" ? value : "";
};

/**
 * Normalizes arbitrary dimension values into a CSS-ready string.
 *
 * - Accepts positive finite numbers and treats them as pixel values.
 * - Accepts strings, trimming whitespace before validating the content.
 * - Returns a pixel string for bare numeric strings (e.g. `"24"` → `"24px"`).
 * - Validates full dimension strings (e.g. `"1.5rem"`) against a whitelist of CSS units.
 * - Rejects empty strings, non-numeric values, unsupported units, and non-positive magnitudes by returning `null`.
 *
 * @param value - Raw dimension input, potentially originating from user or CMS data.
 * @returns A normalized CSS dimension string or `null` if the input is invalid.
 */
const normalizeDimension = (value: unknown): string | null => {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return `${value}px`;
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  // Treat bare numbers as pixel values and .test(...) verifies the trimmed value is purely numeric.
  if (/^\d+(?:\.\d+)?$/.test(trimmedValue)) {
    return `${trimmedValue}px`;
  }

  // Capture the numeric portion (optionally decimal) and the trailing unit; exec(...) exposes both groups.
  const dimensionPattern = /^(\d+(?:\.\d+)?)([a-z%]+)$/i;
  const unitMatch = dimensionPattern.exec(trimmedValue);

  if (!unitMatch) {
    return null;
  }

  const [, numericPart, unit] = unitMatch;

  if (!allowedUnits.has(unit.toLowerCase())) {
    return null;
  }

  if (Number.parseFloat(numericPart) <= 0) {
    return null;
  }

  return `${numericPart}${unit}`;
};

/**
 * Runtime check to ensure alignment matches our accepted values.
 */
const isValidAlignment = (
  alignment: unknown
): alignment is RichTextImageAlignment =>
  alignment === "left" ||
  alignment === "center" ||
  alignment === "right" ||
  alignment === "full";

/**
 * Converts a Storyblok RichTextImage blok into a markup snippet.
 */
const renderRichTextImage = (blok: Record<string, unknown>): string => {
  const typedBlok = blok as GuardedRichTextImageBlock;
  const imageUrl = sanitizeUrl(typedBlok?.image?.filename);

  if (!imageUrl) {
    return "";
  }

  const alignment = isValidAlignment(typedBlok.alignment)
    ? typedBlok.alignment
    : "center";

  const width = normalizeDimension(typedBlok.width);
  const height = normalizeDimension(typedBlok.height);
  // Storyblok stores the toggle as a boolean (wrapText in the component schema).
  const shouldWrapText = typedBlok.wrapText === true;

  const styleTokens: string[] = [];
  if (width) {
    styleTokens.push(`--rt-image-width: ${width}`);
  }
  if (height) {
    styleTokens.push(`--rt-image-height: ${height}`);
  }

  const altText =
    escapeHtml(toStringOrEmpty(typedBlok.alternativeText)) ||
    escapeHtml(toStringOrEmpty(typedBlok?.image?.alt));

  const caption = escapeHtml(toStringOrEmpty(typedBlok.caption)); // Optional, so empty string is fine.

  const figureAttributes: string[] = [
    'class="rich-text-image"',
    'data-component="RichTextImage"',
    `data-align="${alignment}"`,
  ];

  if (shouldWrapText) {
    // Editors can opt in to float the figure alongside surrounding text.
    figureAttributes.push('data-wrap-text="true"');
  }

  if (width) {
    figureAttributes.push('data-has-width="true"');
  }
  if (height) {
    figureAttributes.push('data-has-height="true"');
  }

  const styleAttribute =
    styleTokens.length > 0 ? `style="${styleTokens.join("; ")}"` : null;
  if (styleAttribute) {
    // Only attach a style attribute when width/height were provided.
    figureAttributes.push(styleAttribute);
  }

  const captionMarkup = caption
    ? `<figcaption class="rich-text-image__caption">${caption}</figcaption>`
    : "";

  return `
    <figure ${figureAttributes.join(" ")}>
      <div class="rich-text-image__wrapper">
        <img class="rich-text-image__image" src="${imageUrl}" alt="${altText}" loading="lazy" />
      </div>
      ${captionMarkup}
    </figure>
  `;
};

// Mapping of Storyblok components to their renderer functions.
const componentRenderers: Record<string, StoryblokComponentRenderer> = {
  RichTextImage: renderRichTextImage,
};

/**
 * Renders a Storyblok component node by delegating to a component renderer.
 * Skips nodes that do not match our expected shape.
 */
export const renderRichTextComponent = (
  node: StoryblokComponentNode
): string => {
  const body = node?.attrs?.body;

  if (!Array.isArray(body)) {
    return "";
  }

  return body
    .map((blok) => {
      const componentName =
        typeof blok === "object" && blok && "component" in blok
          ? (blok as { component?: string }).component
          : undefined;

      if (!componentName) {
        return "";
      }

      const renderer = componentRenderers[componentName];

      if (!renderer) {
        // Unknown blok type → Storyblok may introduce new components we do not handle yet.
        return "";
      }

      return renderer(blok as Record<string, unknown>);
    })
    .filter(Boolean)
    .join("");
};
